import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { CircularProgress } from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import { BGC_FIELDS } from 'utils/constants/bgcFields'
import { formatDateTime, maskString } from 'utils/functions'
import { get } from 'services/api'
import { ITEM_STATUS } from 'utils/constants/application'
import ApplicationLogButton from 'components/ResumeTable/ApplicationLogButton'
import DownloadAppButton from 'components/ResumeTable/DownloadAppButton'
import ApplicationUpdateButton from 'components/ResumeTable/ApplicationUpdateButton'
import ApplicationReviewsButton from 'components/ResumeTable/AppliactionReviewsButton'
import Colors from 'utils/Colors'
import ApplicationHelpButton from 'components/ResumeTable/ApplicationHelpButton'
import { reportError } from 'utils/errorReporter'
import BgcExpansible from './BgcExpansible'
import styles from './styles'
import BuildScoreBoard from './BuildScoreBoard'
import ReferenceCheck from './ReferenceCheck'

const   ExpandedInfo = (props) => {
  const { application_id } = props
  const classes = styles()
  const token = useSelector((state) => state.auth.access_token)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  const buildInfo = (title, info, color) => {
    return (
      <div>
        <strong>{`${title}: `}</strong>
        {color == null ? info || 'N/A' : <strong style={{ color: color }}>{info || 'N/A'}</strong>}
      </div>
    )
  }

  const buildGreenhouseStatus = (greenhouseStatus) => {
      const status = { active: 'Active: Em andamento', rejected: 'Rejected: Rejeitado(a)', hired: 'Hired: Contratado(a)' }

    return <div> 
      <strong>Status da Contratação da Greenhouse: </strong>
      {status[greenhouseStatus] || greenhouseStatus || 'N/A'}
  </div>  
  }


  const getData = async () => {
    setLoading(true)

    const res = await get({
      url: `/application?application_uuid=${application_id}`,
      token,
    })

    setData(res)

    setLoading(false)
  }

  const buildCandidateInfo = () => {
    try {
      return (
        <div className={classes.info}>

          {buildInfo('ID Greenhouse', data.candidate.greenhouse_id)}

          {buildInfo('Nome', data.candidate.person.name)}

          {buildInfo('CPF', maskString(data.candidate.person.cpf, 'cpf'))}

          {buildInfo('Nome da Mãe', data.candidate.person.filiation)}

          {buildInfo(
            'Data de Nascimento',
            data.candidate.person.birth_date
              ? moment(data.candidate.person.birth_date).format('DD/MM/YYYY')
              : null,
          )}

        {buildInfo('Recrutador', data.candidate?.recruiter_name || 'Informação indisponível')}

        </div>
      )
    } catch (err) {
      reportError(err)
      return (
        <div className={classes.errorCaptured}>
          <span>Ocorreu um erro ao tratar os dados</span>
        </div>
      )
    }
  }

  const createStatusInfo = () => {
    const translations = {
      'REPROVED': 'Reprovado(a)',
      'REPROVED_IN_DENY_LIST': 'Reprovado(a)',
      'APPROVED': 'Aprovado(a)',

    }
    let status = (data?.result && translations[data.result]) || 'Em andamento'

    if (data.result_reason) {
      status = `${status} - ${data.result_reason}`
    }
    return status
  }
  
  const buildApplicationInfo = () => {
    const applicationStatusColor = () => {
      if (data?.result === 'APPROVED') {
        return Colors.green
      }
      if (data?.result === 'REPROVED') {
        return Colors.red
      }
      return null
    }
    try {
      return (
        <React.Fragment>
          {buildInfo('ID Greenhouse', data.greenhouse_id)}

          <br/>

          {buildInfo('Área', data.occupation_area)}

          {buildInfo('Cargo', data.job_role)}
          
          {buildInfo('Estágio atual', data.current_stage)}

          <br />

          {buildInfo('Status', createStatusInfo(), applicationStatusColor())}

          {buildGreenhouseStatus(data.greenhouse_status)}


          <div className={classes.score}>
            <div>
              <strong>Pontuação parcial: </strong>
              {data.score_board.score_data
                ? `${
                    data.score_board.score_data[BGC_FIELDS.job_role].value +
                    data.score_board.score_data[BGC_FIELDS.occupation_area]
                      .value
                  } ponto(s)`
                : 'N/A'}
            </div>
          </div>
        </React.Fragment>
      )
    } catch (err) {
      reportError(err)
      return (
        <div className={classes.errorCaptured}>
          <span>Ocorreu um erro ao tratar os dados</span>
        </div>
      )
    }
  }

  useEffect(() => {
    getData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application_id])

  return (
    <div className={classes.root}>
      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={64} color="primary" />
          <span className={classes.loadingText}>
            Buscando informações da aplicação...
          </span>
        </div>
      ) : data ? (
        <div className={classes.content}>
          <div className={classes.fixedHeader}>
            <div className={classes.headerButtonRow}>

              <div className={classes.leftHeaderButtonRow}>
                <ApplicationLogButton application={data} useTitle={true} /> 
              
                <DownloadAppButton applicationUUID={data.uuid} canDownload={data.is_report_available} useTitle={true}/>

                <ApplicationReviewsButton application={data} useTitle={true} />

                <ApplicationHelpButton  useTitle={true}/>

              </div>

              <div className={classes.rightHeaderButtonRow}>
                <span style={{ marginBottom: 5 }}><b>Última atualização: </b>{formatDateTime(data.last_changed_date)}</span>

                <span>
                  <ApplicationUpdateButton application={data} useTitle={true}/>
                </span>
              </div>
            </div>
            <hr />
          </div>

          <section className={classes.firstSection}>
            <div className={classes.sectionTitle}>Dados do Candidato</div>

            {buildCandidateInfo()}
          </section>

          <section className={classes.section}>
            <div className={classes.sectionTitle}>Dados da Aplicação</div>

            <div className={classes.info}>{buildApplicationInfo()}</div>
          </section>

          <section className={classes.section}>
            <div className={classes.sectionTitle}>
              Background Check
              {data.background_check?.status === ITEM_STATUS.processing && (
                <AccessTimeIcon style={{ color: '#FFF' }} />
              )}
            </div>

            {data.background_check ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <BuildScoreBoard bgcData={data.background_check} scoreBoardData={data.score_board} isInDenyList={data?.candidate?.person?.is_on_deny_list} />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: 20,
                }}
              >
                A aplicação ainda não atingiu o estágio de Background Check
              </div>
            )}

            {data.background_check &&
              data.background_check.status === 'PROCESSED' && (
                <div className={classes.bgc}>
                  {Object.values(BGC_FIELDS).map((field) => {
                    try {
                      return field !== BGC_FIELDS.job_role &&
                        field !== BGC_FIELDS.occupation_area &&
                        field !== BGC_FIELDS.risk_score ? (
                        <BgcExpansible
                          key={`bgc:${field}`}
                          field={field}
                          bgc_info={data.background_check[field]}
                          score_data={data.score_board.score_data[field]}
                        />
                      ) : null
                    } catch (err) {
                      reportError(err)
                      return (
                        <div className={classes.errorCaptured}>
                          <span>Ocorreu um erro ao tratar os dados</span>
                        </div>
                      )
                    }
                  })}
                </div>
              )}
          </section>

          <section className={classes.section}>
            <div className={classes.sectionTitle}>Análise de Crédito</div>

            {data.reference_check ? (
              <div className={classes.bgc}>
                <ReferenceCheck rfcData={data.reference_check} />
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: 20,
                }}
              >
                A aplicação ainda não atingiu o estágio de Análise de Crédito
              </div>
            )}
          </section>
        </div>
      ) : (
        <div className={classes.notFound}>Nenhuma informação encontrada.</div>
      )}
    </div>
  )
}

export default ExpandedInfo
